import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";
import { PodiumModal } from "./PodiumModal";
import { useAuth } from "../contexts/AuthContext";

const LAST_CHECKED_MATCH_KEY = "last_checked_match_id";

// Check for recent completed matches (within last 48 hours)
async function hasRecentCompletedMatch(): Promise<{
  hasRecent: boolean;
  matchId: string | null;
}> {
  try {
    const now = new Date();
    const fortyEightHoursAgo = new Date(now.getTime() - 48 * 60 * 60 * 1000);
    const timeString = fortyEightHoursAgo.toISOString().slice(0, 16).replace("T", " ");

    // Get the most recent completed match
    const { data, error } = await supabase
      .from("matches")
      .select("id, updated_at, match_time")
      .eq("status", "completed")
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error || !data) {
      return { hasRecent: false, matchId: null };
    }

    // Check if match was completed within last 48 hours
    const matchUpdatedAt = new Date(data.updated_at);
    const isRecent = matchUpdatedAt >= fortyEightHoursAgo;

    return { hasRecent: isRecent, matchId: data.id };
  } catch (error) {
    console.error("Error checking for recent completed match:", error);
    return { hasRecent: false, matchId: null };
  }
}

export function PodiumModalHandler() {
  const { user, loading: authLoading } = useAuth();
  const [showPodiumModal, setShowPodiumModal] = useState(false);
  const prevUserRef = useRef<string | null>(null);
  const hasCheckedRef = useRef(false);

  const handleStartPredicting = useCallback(() => {
    setShowPodiumModal(false);
    // Modal closes - user can navigate to matches from the landing screen
  }, []);

  useEffect(() => {
    // Only check when user logs in (was null, now has user)
    const justLoggedIn =
      !authLoading && !prevUserRef.current && user && !hasCheckedRef.current;

    if (justLoggedIn) {
      hasCheckedRef.current = true;
      prevUserRef.current = user.id;

      let timer: NodeJS.Timeout | null = null;

      // Check if we should show the podium modal
      hasRecentCompletedMatch().then(({ hasRecent, matchId }) => {
        if (hasRecent && matchId) {
          // Always show modal on login if there's a recent completed match
          // Small delay to ensure smooth UI after login
          timer = setTimeout(() => {
            setShowPodiumModal(true);
            localStorage.setItem(LAST_CHECKED_MATCH_KEY, matchId);
          }, 1000);
        }
      });

      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    } else if (!user) {
      // Reset when user logs out
      prevUserRef.current = null;
      hasCheckedRef.current = false;
    }
  }, [user, authLoading]);

  return (
    <PodiumModal
      isOpen={showPodiumModal}
      onClose={() => setShowPodiumModal(false)}
      onStartPredicting={handleStartPredicting}
    />
  );
}

